import { useOnScroll } from '@mm/hooks/src/use-on-scroll';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INTEGRATIONS_SECTIONS, PageSettingDefinition } from '../constants';
import styles from './IntegrationsPanel.module.css';
import { canUseFacebookAdsSyncIntegrationAndWidget } from '@mm/state/selectors/adAccountsSelecotrs';
import { useSelector } from 'react-redux';
import { selectIsShopifyContext } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { Button } from '@mm/components';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { HelpCenter } from '../../../../../../common/components/HelpCenter/HelpCenter';

export interface IntegrationSectionProps {
  section?: PageSettingDefinition;
}

export type IntegrationLinkType =
  | 'email'
  | 'native-integrations'
  | 'contact-suppression'
  | 'unsubscribe-list'
  | 'notifications'
  | 'facebook'
  | 'google-ads'
  | 'xray';

export const IntegrationsPanel = () => {
  const canUseFacebookAds = useSelector(
    canUseFacebookAdsSyncIntegrationAndWidget,
  );
  const subscriptionFeatures = useSelector(
    (state: StoreState) => state.featureFlags.subscriptionFeatureFlags,
  );

  const { invalidateAll } = useConnectionCRUDActions();
  const { openNewBillingModal2023 } = useModalActions();
  const integrationsContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollManually, setScrollManually] = useState(true);
  const history = useHistory();
  const location = useLocation<{ kind: IntegrationLinkType }>();
  const locationKindParam = location.state?.kind;

  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

  const getVisibleIds = () =>
    INTEGRATIONS_SECTIONS.map(({ id }) => {
      const el = document.getElementById(id);

      if (el) {
        const rect = el.getBoundingClientRect();
        const top = rect.y;
        const [max] = [window.innerHeight * 0.5];

        return top < max ? id : undefined;
      }

      return undefined;
    }).filter(Boolean);

  const onPageScroll = () => {
    if (scrollManually) {
      const visibleIds = getVisibleIds();
      const lastElementId = visibleIds?.[visibleIds.length - 1];

      if (lastElementId) {
        const el: HTMLElement | null = document.querySelector(
          `[href='#${lastElementId}']`,
        );
        if (el) {
          el?.focus();
        }
      }
    }
  };

  useOnScroll(integrationsContainerRef.current, onPageScroll);

  const scrollToElement = (selector: IntegrationLinkType) => {
    document.getElementById(selector)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const onUpgrade = landmineName => {
    openNewBillingModal2023({
      landmineName,
    });
  };

  const isShopify = useSelector(selectIsShopifyContext);

  const sectionsToRender = useMemo(
    () =>
      INTEGRATIONS_SECTIONS?.filter(v =>
        isShopify ? !['email', 'facebook'].includes(v.id) : true,
      ),
    [isShopify],
  );

  useLayoutEffect(() => {
    if (
      locationKindParam &&
      INTEGRATIONS_SECTIONS &&
      INTEGRATIONS_SECTIONS.find(section => section.id === locationKindParam)
    ) {
      setScrollManually(false);
      scrollToElement(locationKindParam);
      history.replace({ state: {} });
    }
    return () => {
      invalidateAll();
    };
  }, [locationKindParam]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div
          className={styles.integrationsContainer}
          ref={integrationsContainerRef}
        >
          {sectionsToRender.map(section => {
            const Component: any = section.component;
            const hasRequiredFeatureFlag =
              section?.requiredFeature &&
              subscriptionFeatures.includes(section?.requiredFeature);
            const sectionName =
              section.id === 'xray' ? pixelName : section.name;

            if (section.id === 'facebook' && !canUseFacebookAds) {
              return <></>;
            }

            return (
              <section id={section.id} key={section.id}>
                <div>
                  <div className={styles.titleContainer}>
                    <div className={styles.title}>{sectionName}</div>
                    {section.helpDoc && (
                      <HelpCenter
                        helpLink={section.helpDoc}
                        className={styles.help}
                      />
                    )}
                  </div>
                  <div>{section.subtitle}</div>
                </div>
                <div className={styles.sectionContainer}>
                  <div
                    className={classNames({
                      [styles.noPointerEvents]: !hasRequiredFeatureFlag,
                    })}
                  >
                    <Component section={section} />
                  </div>
                  {!hasRequiredFeatureFlag && (
                    <Button
                      css={{ container: styles.upgradeButton }}
                      onClick={() => onUpgrade(section.landmineName)}
                    >
                      Upgrade
                    </Button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};
